import { useEffect, useState } from "react";
import api, { formatApiError, bioPhoto } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import { PageHeader, Card, EmptyState, Field } from "@/components/common";
import { Avatar } from "@/components/Avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Pencil, IdCard } from "lucide-react";
import { DeleteButton } from "@/components/DeleteButton";
import { CC_ROLES } from "@/lib/constants";

export default function ClinicalInstructors() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const isCC = CC_ROLES.includes(user.role);
  const isFacility = user.role === "Clinical Instructor";
  const isDor = user.role === "DOR";
  const canManage = isCC || isFacility || isDor;
  const picksFacility = isCC || (isDor && facilities.length > 1);

  const load = () => api.get("/clinical-instructors").then(({ data }) => setRows(data));
  useEffect(() => {
    load();
    if (canManage) api.get("/facilities").then(({ data }) => setFacilities(data));
  }, []);

  const openNew = () => {
    setEditing(null);
    setForm({ facility_id: isFacility ? user.organization_id : (isDor && facilities.length === 1 ? facilities[0].id : undefined), student_capacity: 2 });
    setOpen(true);
  };
  const openEdit = (c) => { setEditing(c); setForm({ ...c, disc_str: (c.disciplines_supported || []).join(", ") }); setOpen(true); };

  const save = async () => {
    try {
      if (!form.name?.trim()) return toast.error("Give the clinical instructor a name.");
      const facilityId = form.facility_id
        || (isFacility ? user.organization_id : null)
        || (isDor && facilities.length === 1 ? facilities[0].id : null);
      if (!facilityId) return toast.error("Pick the facility this instructor works at.");
      const payload = {
        name: form.name, credentials: form.credentials, email: form.email, phone: form.phone,
        facility_id: facilityId, bio: form.bio, student_capacity: Number(form.student_capacity) || 1,
        disciplines_supported: (form.disc_str || "").split(",").map((s) => s.trim()).filter(Boolean),
        rotation_levels_supported: form.rotation_levels_supported || ["Level I", "Level II"],
      };
      if (editing) { await api.patch(`/clinical-instructors/${editing.id}`, payload); toast.success("Updated"); }
      else { await api.post("/clinical-instructors", payload); toast.success("Clinical instructor added"); }
      setOpen(false); setForm({}); setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const facName = (id) => facilities.find((f) => f.id === id)?.name;

  return (
    <div>
      <PageHeader title="Clinical Instructors" subtitle="Managed CI records & capacity">
        {canManage && <Button onClick={openNew} className="bg-cc-blue text-white" data-testid="add-ci-btn">+ Add Clinical Instructor</Button>}
      </PageHeader>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {rows.length === 0 && <Card><EmptyState text="No clinical instructors." /></Card>}
        {rows.map((c) => (
          <Card key={c.id} className="p-0">
            <div className="p-5">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2.5">
                  <Avatar src={c.user_id ? bioPhoto(c.user_id) : null} name={c.name} size={34} testId={`ci-avatar-${c.id}`} />
                  <h3 className="font-medium text-zinc-900">{c.name} <span className="text-xs text-zinc-400">{c.credentials}</span></h3>
                </div>
                <div className="flex items-center">
                {canManage && <button onClick={() => openEdit(c)} data-testid={`edit-ci-${c.id}`} className="text-zinc-400 hover:text-cc-blue"><Pencil className="w-4 h-4" /></button>}
                {canManage && <DeleteButton endpoint={`/clinical-instructors/${c.id}`} label="clinical instructor" name={c.name} onDeleted={load} testId={`delete-ci-${c.id}`} className="ml-2" />}
                </div>
              </div>
              <p className="text-sm text-zinc-500">{c.email}</p>
              {facName(c.facility_id) && <p className="text-xs text-zinc-400 mt-0.5">{facName(c.facility_id)}</p>}
              <div className="flex flex-wrap gap-1 mt-3">
                {(c.disciplines_supported || []).map((d) => <span key={d} className="text-[10px] font-mono px-1.5 py-0.5 bg-cc-green/10 text-cc-green rounded">{d}</span>)}
              </div>
              <div className="mt-3 flex items-center justify-between text-sm">
                <span className="text-zinc-500">Capacity</span>
                <span className="font-mono">{c.current_students}/{c.student_capacity}</span>
              </div>
              {isCC && c.user_id && (
                <Link to={`/bios/${c.user_id}`} className="mt-3 inline-flex items-center gap-1.5 text-sm text-cc-blue hover:underline" data-testid={`view-ci-bio-${c.id}`}>
                  <IdCard className="w-4 h-4" />View / share bio
                </Link>
              )}
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editing ? "Edit Clinical Instructor" : "Add Clinical Instructor"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Name"><Input data-testid="ci-name" value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
            <Field label="Credentials"><Input placeholder="DPT, OTR/L" value={form.credentials || ""} onChange={(e) => setForm({ ...form, credentials: e.target.value })} /></Field>
            <Field label="Email"><Input value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
            <Field label="Phone"><Input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Field>
            {picksFacility && (
              <Field label="Facility">
                <Select value={form.facility_id} onValueChange={(v) => setForm({ ...form, facility_id: v })}>
                  <SelectTrigger data-testid="ci-facility"><SelectValue placeholder="Select facility" /></SelectTrigger>
                  <SelectContent>{facilities.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
            )}
            <Field label="Disciplines (comma sep)"><Input placeholder="PT, OT" value={form.disc_str || ""} onChange={(e) => setForm({ ...form, disc_str: e.target.value })} /></Field>
            <Field label="Student capacity"><Input type="number" value={form.student_capacity ?? 2} onChange={(e) => setForm({ ...form, student_capacity: e.target.value })} /></Field>
            <div className="col-span-2"><Field label="Bio"><Textarea value={form.bio || ""} onChange={(e) => setForm({ ...form, bio: e.target.value })} /></Field></div>
          </div>
          <DialogFooter><Button onClick={save} className="bg-cc-blue text-white" data-testid="save-ci-btn">{editing ? "Save Changes" : "Add"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
